import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { getFreshAccessToken } from '@/lib/apiClient';
import { mockBus } from '@/mocks/bus';
import { isMockApiEnabled } from '@/mocks/env';
import type { ConnectionStatus, RealtimeEventName, RealtimeEventPayloads } from '@/types';

const WS_URL: string = import.meta.env.VITE_WS_URL || window.location.origin;

type StatusListener = (status: ConnectionStatus) => void;

/**
 * Socket.IO event map for the inbound direction.
 *
 * Without it the socket is typed with `DefaultEventsMap`, and `socket.on(event,
 * handler)` cannot be checked when `event` is a generic parameter — which is
 * exactly how `on()` below is written.
 */
type ServerToClientEvents = {
  [E in RealtimeEventName]: (payload: RealtimeEventPayloads[E]) => void;
};

/**
 * Single Socket.IO connection shared by the whole app.
 *
 * A module-level singleton rather than a React context value: several hooks
 * subscribe independently (dashboard widgets, ticket pages, the notification
 * bell) and each mount must not open its own socket. Reference counting in
 * `connect`/`disconnect` keeps the connection alive while at least one
 * consumer is mounted.
 */
class RealtimeService {
  private socket: Socket<ServerToClientEvents> | null = null;
  private consumers = 0;
  private status: ConnectionStatus = 'disconnected';
  private readonly statusListeners = new Set<StatusListener>();
  /** True while the mock event bus stands in for the socket. */
  private mocked = false;

  getStatus(): ConnectionStatus {
    return this.status;
  }

  onStatusChange(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((listener) => listener(status));
  }

  /** Opens the connection (or joins the existing one) and bumps the ref count. */
  async connect(): Promise<void> {
    this.consumers += 1;
    if (this.socket) return;

    const token = await getFreshAccessToken();
    if (!token) {
      this.setStatus('error');
      return;
    }

    if (isMockApiEnabled()) {
      const { mockRealtime, resolveToken } = await import('@/mocks');
      const user = resolveToken(token);
      if (!user) {
        this.setStatus('error');
        return;
      }

      this.mocked = true;
      mockRealtime.start(user);
      this.setStatus('connected');
      return;
    }

    this.setStatus('connecting');
    // `io()` is not generic in socket.io-client 4.8; the Socket instance is.
    this.socket = io(WS_URL, {
      path: '/socket.io',
      // Default transports (polling → websocket) survive nginx/Vite/gateway hops.
      // The gateway reads the JWT to join this socket to its tenant room; the
      // frontend never asks for a tenant explicitly (RLS owns that decision).
      auth: { token },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 15_000,
      timeout: 10_000,
    }) as Socket<ServerToClientEvents>;

    this.socket.on('connect', () => this.setStatus('connected'));
    this.socket.on('disconnect', () => this.setStatus('disconnected'));
    this.socket.on('connect_error', () => this.setStatus('error'));

    // The access token can expire while the socket is open; refresh it before
    // each reconnect attempt so the handshake does not fail with a stale JWT.
    this.socket.io.on('reconnect_attempt', () => {
      void getFreshAccessToken().then((fresh) => {
        if (fresh && this.socket) this.socket.auth = { token: fresh };
      });
    });
  }

  /** Decrements the ref count and tears down when nobody is listening. */
  disconnect(): void {
    this.consumers = Math.max(0, this.consumers - 1);
    if (this.consumers > 0) return;

    if (this.mocked) {
      void import('@/mocks').then(({ mockRealtime }) => mockRealtime.stop());
      this.mocked = false;
      this.setStatus('disconnected');
      return;
    }

    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.setStatus('disconnected');
  }

  /** Subscribes to one typed event. Returns an unsubscribe function. */
  on<E extends RealtimeEventName>(event: E, handler: (payload: RealtimeEventPayloads[E]) => void): () => void {
    // The mock bus is checked first and synchronously: subscriptions are set up
    // in the same effect that calls `connect`, which resolves a tick later.
    if (isMockApiEnabled()) return mockBus.on(event, handler);

    // Socket.IO's `on` unions reserved events (`connect`, …) with the app map,
    // so a generic `E extends RealtimeEventName` cannot be proven by tsc.
    this.socket?.on(event, handler as never);
    return () => {
      this.socket?.off(event, handler as never);
    };
  }

  /** Forces a fresh handshake, e.g. right after login. */
  async reset(): Promise<void> {
    if (this.mocked) {
      const { mockRealtime, resolveToken } = await import('@/mocks');
      const token = await getFreshAccessToken();
      const user = token ? resolveToken(token) : null;
      if (user) mockRealtime.start(user);
      return;
    }

    if (!this.socket) return;
    const consumers = this.consumers;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.consumers = 0;
    for (let i = 0; i < consumers; i += 1) await this.connect();
  }
}

export const realtimeService = new RealtimeService();
